import { useState, useEffect, useCallback } from "react";
import { invokeWithRetry } from "@/lib/invokeWithRetry";
import { useAuth } from "@/hooks/useAuth";
import { logger } from "@/lib/logger";

interface BackendSecrets {
  productionMode: boolean;
  resendApiKey: boolean;
  azureTenantId: boolean;
  azureClientId: boolean;
  azureClientSecret: boolean;
}

interface SetupValidation {
  authTriggersConfigured: boolean;
  cronJobsConfigured: boolean;
  missingAuthTriggers: string[];
  missingOptionalAuthTriggers: string[];
  missingCronJobs: string[];
  missingOptionalCronJobs: string[];
  checkPerformed: boolean;
  backendSecrets?: BackendSecrets;
}

interface SetupValidationState {
  isLoading: boolean;
  hasChecked: boolean;
  validation: SetupValidation | null;
  error: string | null;
}

type StoredSetupValidation = Omit<SetupValidation, "backendSecrets">;

const STORAGE_KEY = "setup_validation_checked";
const STORAGE_EXPIRY_HOURS = 24;

const checkedState = (
  validation: SetupValidation | StoredSetupValidation | null,
  error: string | null = null,
): SetupValidationState => ({
  isLoading: false,
  hasChecked: true,
  validation,
  error,
});

const toStoredValidation = (validation: SetupValidation): StoredSetupValidation => {
  const safeValidation = { ...validation };
  delete safeValidation.backendSecrets;
  return safeValidation;
};

const cacheSetupValidation = (
  validation: SetupValidation,
  allConfigured: boolean,
  dismissed = false,
): void => {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      timestamp: Date.now(),
      allConfigured,
      validation: toStoredValidation(validation),
      ...(dismissed ? { dismissed: true } : {}),
    }),
  );
};

const readStoredValidation = (): StoredSetupValidation | null | undefined => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) {
    return undefined;
  }

  try {
    const { validation } = JSON.parse(stored) as { validation?: StoredSetupValidation };
    return validation ?? null;
  } catch {
    return undefined;
  }
};

const hasSetupConfigurationGap = (validation: SetupValidation): boolean =>
  !validation.authTriggersConfigured || !validation.cronJobsConfigured;

const shouldShowSetupWarning = (validation: SetupValidation | null): boolean => {
  if (!validation?.checkPerformed) {
    return false;
  }

  return hasSetupConfigurationGap(validation);
};

const getMissingRequiredSecrets = (backendSecrets?: BackendSecrets): string[] => {
  if (backendSecrets?.resendApiKey === false) {
    return ["RESEND_API_KEY"];
  }

  return [];
};

const shouldShowSecretWarning = (
  validation: SetupValidation | null,
  missingRequiredSecrets: string[],
): boolean => Boolean(validation?.checkPerformed) && missingRequiredSecrets.length > 0;

const canCheckSetup = (user: unknown, isAdmin: boolean): boolean => Boolean(user) && isAdmin;

const getCachedState = (
  force: boolean,
  shouldSkipCheck: () => boolean,
): SetupValidationState | null => {
  if (force || !shouldSkipCheck()) {
    return null;
  }

  const validation = readStoredValidation();
  return validation === undefined ? null : checkedState(validation);
};

const toValidation = (
  data?: {
    setupValidation?: SetupValidation;
    backendSecrets?: BackendSecrets;
  } | null,
): SetupValidation | null =>
  data?.setupValidation
    ? {
        ...data.setupValidation,
        backendSecrets: data.backendSecrets,
      }
    : null;

const getSetupErrorMessage = (err: unknown): string =>
  err instanceof Error ? err.message : "Unknown error";

export function useSetupValidation() {
  const { user, isAdmin } = useAuth();
  const [state, setState] = useState<SetupValidationState>({
    isLoading: false,
    hasChecked: false,
    validation: null,
    error: null,
  });

  // Check if we should skip validation (already checked recently)
  const shouldSkipCheck = useCallback((): boolean => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return false;

    try {
      const { timestamp, allConfigured } = JSON.parse(stored);
      const hoursSinceCheck = (Date.now() - timestamp) / (1000 * 60 * 60);

      // If all was configured and checked within expiry time, skip
      if (allConfigured && hoursSinceCheck < STORAGE_EXPIRY_HOURS) {
        return true;
      }
      // If there were issues, check again after 1 hour
      if (!allConfigured && hoursSinceCheck < 1) {
        return true;
      }
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
    return false;
  }, []);

  // Perform the validation check
  const checkSetup = useCallback(
    async (force: boolean = false) => {
      // Only check for admins
      if (!canCheckSetup(user, isAdmin)) {
        setState(checkedState(null));
        return;
      }

      // Skip if already checked recently (unless forced)
      const cachedState = getCachedState(force, shouldSkipCheck);
      if (cachedState) {
        setState(cachedState);
        return;
      }

      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        const { data, error } = await invokeWithRetry<{
          setupValidation?: SetupValidation;
          backendSecrets?: BackendSecrets;
        }>("check-dev-mode");

        if (error) {
          throw error;
        }

        const validation = toValidation(data);

        if (validation?.checkPerformed) {
          const allConfigured = validation.authTriggersConfigured && validation.cronJobsConfigured;

          // Store result
          cacheSetupValidation(validation, allConfigured);

          setState(checkedState(validation));
        } else {
          setState(checkedState(null));
        }
      } catch (err) {
        logger.error("Setup validation error:", err);
        setState(checkedState(null, getSetupErrorMessage(err)));
      }
    },
    [user, isAdmin, shouldSkipCheck],
  );

  const shouldAutoCheck = [Boolean(user), isAdmin, !state.hasChecked, !state.isLoading].every(
    Boolean,
  );

  // Auto-check after login (only for admins)
  useEffect(() => {
    if (shouldAutoCheck) {
      checkSetup();
    }
  }, [shouldAutoCheck, checkSetup]);

  // Dismiss the warning (mark as acknowledged)
  const dismissWarning = useCallback(() => {
    if (state.validation) {
      cacheSetupValidation(state.validation, false, true);
      setState((prev) => ({ ...prev, validation: null }));
    }
  }, [state.validation]);

  // Check if warning should be shown (setup issues)
  const showWarning = shouldShowSetupWarning(state.validation);

  // Check if secrets warning should be shown (missing required secrets)
  const missingRequiredSecrets = getMissingRequiredSecrets(state.validation?.backendSecrets);
  const showSecretsWarning = shouldShowSecretWarning(state.validation, missingRequiredSecrets);

  return {
    ...state,
    showWarning,
    showSecretsWarning,
    missingRequiredSecrets,
    checkSetup,
    dismissWarning,
  };
}
